import { CreditNoteRepository } from "../../domain/repositories/CreditNoteRepository";
import {
  CreditNoteEntity,
  CreditNoteDataEntity,
  CreditNoteOperationResult,
  CreditNotesResultEntity,
  CreditNoteCountsEntity,
  CreditsAppliedResultEntity,
  CreditNoteDocumentType,
  CreditNoteListStatus,
} from "../../domain/entities/CreditNoteEntity";
import {
  getCreatedInBackendSuccessMessage,
  getFailedCreateInBackendMessage,
  getFailedUpdateAndPostMessage,
  getFailedUpdateInBackendMessage,
  getStoredAccountingBackend,
  getUpdatedAndPostedSuccessMessage,
  getUpdatedInBackendSuccessMessage,
} from "../../../../config/accounting";
import {
  fetchCreditNotes,
  updateCreditNoteJson,
  createZohoCreditNote,
  updateZohoCreditNote,
  rejectCreditNote,
  approveCreditNote,
  saveCreditNoteAsDraft,
  fetchCreditNoteCounts,
  deleteCreditNote,
  getCreditNoteById,
} from "../api/creditNotesApi";
import { CreditNoteMapper } from "../mappers/CreditNoteMapper";

export class CreditNoteRepositoryImpl implements CreditNoteRepository {
  /**
   * Fetch credit notes through the unified list endpoint.
   */
  async fetchCreditNotes(params?: {
    limit?: number;
    offset?: number;
    status?: CreditNoteListStatus;
    search?: string;
    date_from?: string;
    date_to?: string;
    sort_by?: "created_at" | "updated_at" | "document_number" | "status";
    sort_order?: "asc" | "desc";
    document_type?: CreditNoteDocumentType;
  }): Promise<CreditNotesResultEntity> {
    const response = await fetchCreditNotes(params);
    return CreditNoteMapper.toCreditNotesResultEntity(response);
  }

  /**
   * Get a single credit note by ID
   */
  async getCreditNoteById(
    creditNoteId: number | string
  ): Promise<CreditNoteEntity> {
    const response = await getCreditNoteById(creditNoteId);
    // Use CreditNoteMapper.toCreditNoteEntityWithCredits() to include credits applied
    return CreditNoteMapper.toCreditNoteEntityWithCredits(
      response.data || response
    );
  }

  /**
   * Update credit note JSON data (for auto-save and manual edits)
   */
  async updateCreditNoteJson(
    creditNoteId: number,
    creditNoteData: CreditNoteDataEntity
  ): Promise<CreditNoteOperationResult> {
    try {
      // Call API
      await updateCreditNoteJson(creditNoteId, creditNoteData);

      return {
        success: true,
        message: "Credit note updated successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to update credit note",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Create credit note in Zoho Books
   * Creates a new credit note in the database and pushes it to Zoho Books
   * If creditNoteId is provided, updates the existing credit note instead
   */
  async createZohoCreditNote(
    creditNoteData: CreditNoteDataEntity,
    pdfUrl?: string,
    creditNoteId?: number
  ): Promise<CreditNoteOperationResult> {
    try {
      const backend = getStoredAccountingBackend();
      // Call API with optional creditNoteId for updating existing drafts
      await createZohoCreditNote(creditNoteData, pdfUrl, creditNoteId);

      return {
        success: true,
        message: creditNoteId
          ? getUpdatedAndPostedSuccessMessage("Credit note", backend)
          : getCreatedInBackendSuccessMessage("Credit note", backend),
      };
    } catch (error: any) {
      const backend = getStoredAccountingBackend();
      return {
        success: false,
        message: creditNoteId
          ? getFailedUpdateAndPostMessage("credit note", backend)
          : getFailedCreateInBackendMessage("credit note", backend),
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Update credit note in Zoho Books
   */
  async updateZohoCreditNote(
    creditNoteId: number | string,
    creditNoteData: CreditNoteDataEntity
  ): Promise<CreditNoteOperationResult> {
    try {
      const backend = getStoredAccountingBackend();
      // Call API
      await updateZohoCreditNote(creditNoteId, creditNoteData);

      return {
        success: true,
        message: getUpdatedInBackendSuccessMessage("Credit note", backend),
      };
    } catch (error: any) {
      const backend = getStoredAccountingBackend();
      return {
        success: false,
        message: getFailedUpdateInBackendMessage("credit note", backend),
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Reject a credit note
   */
  async rejectCreditNote(
    creditNoteId: number
  ): Promise<CreditNoteOperationResult> {
    try {
      // Call API
      await rejectCreditNote(creditNoteId);

      return {
        success: true,
        message: "Credit note rejected successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to reject credit note",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Approve a credit note
   */
  async approveCreditNote(
    creditNoteId: number
  ): Promise<CreditNoteOperationResult> {
    try {
      // Call API
      await approveCreditNote(creditNoteId);

      return {
        success: true,
        message: "Credit note approved successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to approve credit note",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Fetch credit note counts for dashboard cards
   */
  async fetchCreditNoteCounts(params?: {
    date_from?: string;
    date_to?: string;
    document_type?: CreditNoteDocumentType;
  }): Promise<CreditNoteCountsEntity> {
    const response = await fetchCreditNoteCounts(params);
    return CreditNoteMapper.toCreditNoteCountsEntity(response);
  }

  /**
   * Save credit note as draft
   * Creates a new credit note with draft status (0)
   */
  async saveCreditNoteAsDraft(
    creditNoteData: CreditNoteDataEntity,
    options?: {
      jobid?: string | null;
      folder_id?: string | null;
      company_id?: number | null;
      gst_rates?: any;
      gst_types?: any;
      is_tds_applied?: number;
      tds_tax_id?: string | null;
      tds_tax_amount?: number | null;
      tds_tax_percentage?: number | null;
      tds_tax_name?: string | null;
      tds_section?: string | null;
    }
  ): Promise<CreditNoteEntity> {
    try {
      const response = await saveCreditNoteAsDraft(creditNoteData, options);

      if (response.success && response.data?.credit_note) {
        // Map the response to CreditNoteEntity
        const creditNote = response.data.credit_note;
        return CreditNoteMapper.toCreditNoteEntity(creditNote);
      } else {
        throw new Error(
          response.message || "Failed to save credit note as draft"
        );
      }
    } catch (error: any) {
      throw new Error(error.message || "Failed to save credit note as draft");
    }
  }

  /**
   * Get credits applied to invoice or bill
   * Returns credits applied data along with invoice/bill total
   */
  async getCreditsApplied(
    creditNoteId: number
  ): Promise<CreditsAppliedResultEntity | null> {
    // This is already included in getCreditNoteById response
    // But we can implement separately if needed for refresh
    const creditNote = await this.getCreditNoteById(creditNoteId);
    return creditNote.creditsApplied || null;
  }

  /**
   * Delete credit note (unified endpoint)
   * Handles both draft (DB only) and posted (unapply + Zoho delete) credit notes
   * Accepts both internal numeric ids and Zoho ids
   */
  async deleteCreditNote(
    creditNoteId: number | string
  ): Promise<CreditNoteOperationResult> {
    try {
      await deleteCreditNote(creditNoteId);
      return {
        success: true,
        message: "Credit note deleted successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to delete credit note",
        error: error.message || "Unknown error",
      };
    }
  }
}
